import type { ReactNode } from 'react';
import { env } from '@/config/env';
import { EnvelopeIcon } from '@/icons';

/**
 * Centered-card shell for auth screens on a branded split background. No nav.
 * Fully responsive: the brand panel collapses on small viewports.
 */
export function AuthLayout({ children, title, subtitle }: {
  children: ReactNode;
  title: string;
  subtitle?: string;
}) {
  return (
    <div className="flex min-h-screen bg-bg text-fg">
      {/* Brand panel (hidden on small screens) */}
      <aside className="relative hidden w-1/2 flex-col justify-between bg-brand p-12 text-brand-fg lg:flex">
        <div className="flex items-center gap-3">
          <EnvelopeIcon className="h-8 w-8" aria-hidden="true" />
          <span className="text-xl font-semibold">{env.appName}</span>
        </div>
        <div className="max-w-md">
          <h2 className="text-3xl font-bold leading-tight">Enterprise mail, under control.</h2>
          <p className="mt-4 text-brand-fg/80">
            Domains, mailboxes, deliverability and health — one administration console.
          </p>
        </div>
        <p className="text-sm text-brand-fg/70">© {new Date().getFullYear()} {env.appName}</p>
      </aside>

      {/* Form panel */}
      <main className="flex flex-1 items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-sm">
          <div className="mb-8 lg:hidden">
            <div className="flex items-center gap-2 text-brand">
              <EnvelopeIcon className="h-7 w-7" aria-hidden="true" />
              <span className="text-lg font-semibold text-fg">{env.appName}</span>
            </div>
          </div>
          <h1 className="text-2xl font-semibold text-fg">{title}</h1>
          {subtitle && <p className="mt-2 text-sm text-fg-muted">{subtitle}</p>}
          <div className="mt-8">{children}</div>
        </div>
      </main>
    </div>
  );
}
